import { useRouter } from "next/router";
import * as Data14 from "common/src/data/Data14.bs";
import * as Sd from "common/src/tft-static/Sd";
import { traitRankCounts } from "common/src/tft-static/Sd";
import { lastPatch, statsSet } from "common/src/Config";
import { deepEqual } from "fast-equals";
import { useReducer, useCallback, useMemo, useEffect, useRef } from "react";
import { sendMsg } from "common/src/SnackbarService.bs";
import * as Search from "../common/Search2";
import * as T from "common/src/bindings/Translation2";
import {
  GameMode,
  FilterUnit,
  FilterTrait,
  FilterItem,
  FilterAugment,
  Filter,
  encodeQuery,
  decodeQuery,
} from "./CoQuery";

type Qf = {
  typ: string;
  value: string;
  tier?: string;
  unitId?: [string, number];
  exclude: boolean;
  itemCount: undefined | string;
};

const patreonErrMsg =
  "This is a Patreon only feature. You can find instructions on connecting to Patreon by clicking on Patreon logo at top right. If you are already a Patron try reconnecting to Patreon, if it doesn't help report the bug to Discord.";

const filtersToQuery = (
  set: number,
  filters: Array<Filter>,
  exclude: boolean,
): Array<Qf> => {
  const query: Array<Qf> = [];
  filters.forEach((f) => {
    switch (f.typ) {
      case "u":
        query.push({
          typ: "u",
          value: f.id,
          tier: String(f.starLevel),
          itemCount: f.itemCount,
          exclude,
        });
        break;
      case "t":
        if (exclude && f.rank === "0") {
          traitRankCounts(f.id, set)?.forEach((_, idx) => {
            query.push({
              typ: "t",
              value: f.id,
              tier: String(idx + 1),
              itemCount: undefined,
              exclude,
            });
          });
        } else {
          query.push({
            typ: "t",
            value: f.id,
            tier: f.rank,
            itemCount: undefined,
            exclude,
          });
        }
        break;
      case "i":
        query.push({
          typ: "i",
          value: f.id,
          unitId: f.unitId,
          itemCount: undefined,
          exclude,
        });
        break;
      case "a":
        query.push({
          typ: "a",
          value: f.id,
          tier: String(f.pick),
          itemCount: undefined,
          exclude,
        });
        break;
    }
  });

  return query;
};

export type Dp = {
  count: number;
  delta: number;
  place: number;
  top4: number;
  won: number;
};

export type ItemBuild = {
  unit: string;
  stars: number;
  items: Array<string>;
  data: Dp;
};

export type Board = {
  matchId: string;
  place: number;
  augments: Array<string>;
  units: Array<[string, number, Array<string>]>;
};

export type ExplData = {
  units: Array<[string, Dp]>;
  starUnits: Array<[string, number, Dp]>;
  traits: Array<[string, number, Dp]>;
  extraTraits: Array<[string, Dp]>;
  baseItems: Array<[string, Dp]>;
  unitItems: Array<[string, number, string, Dp]>;
  unitItemCounts: Array<[string, number, Dp, Dp, Dp, Dp]>;
  augments: Array<[string, Dp, Dp | null, Dp | null, Dp | null]>;
  base: {
    count: number;
    place: number;
    top4: number;
    won: number;
  };
  plDist: Array<number>;
  level: number;
  levels: Array<[number, Dp]>;
  portals: Array<[number, Dp]>;
  itemBuilds: Array<ItemBuild>;
  oppUnits: Array<[string, Dp, Dp, Dp, Dp]>;
  matches: Array<Board>;
  exalteds: Array<[number, Dp, Dp, Dp]>;
};

const getQueueId = (gameMode: GameMode) => {
  if (gameMode === "ranked") {
    return 1100;
  } else if (gameMode === "hr") {
    return 1130;
  } else if (gameMode === "duo") {
    return 1160;
  } else if (gameMode === "pbe") {
    return 1090;
  } else if (gameMode === "temp") {
    return 6110;
  } else {
    return 1;
  }
};

export type State = {
  isPatron: boolean;
  availablePatches: Array<number>;
  filters: Array<Filter>;
  excludeFilters: Array<Filter>;
  queryData: undefined | [Array<Filter>, Array<Filter>, ExplData | undefined];
  loading: boolean;
  patch: number;
  rankGroup: number;
  levels: Array<number>;
  lastRound: string | null;
  itemCount: string | null;
  portals: Array<number>;
  regions: Array<string>;
  gameMode: GameMode;
  gameType: number;
  date: number;
};

const checkItems = (filters: Array<Filter>): Array<Filter> => {
  return filters.filterMap((f) => {
    if (f.typ === "i") {
      const fUnitId = f.unitId;
      if (fUnitId == null) {
        return f;
      }

      if (
        filters.some(
          (f2) =>
            f2.typ === "u" &&
            f2.id === fUnitId[0] &&
            f2.starLevel === fUnitId[1],
        )
      ) {
        return f;
      } else {
        const sameUnit = filters.find(
          (f2): f2 is FilterUnit => f2.typ === "u" && f2.id === fUnitId[0],
        );
        if (sameUnit) {
          return {
            ...f,
            unitId: [sameUnit.id, sameUnit.starLevel],
          };
        } else {
          return {
            ...f,
            unitId: undefined,
          };
        }
      }
    } else {
      return f;
    }
  });
};

export type Action =
  | ["set_available_patches", Array<number>]
  | ["add_filter", Filter]
  | ["set_build", ItemBuild]
  | ["set_filter", [number, Filter | undefined]]
  | ["set_exclude_filter", [number, Filter | undefined]]
  | ["set_query_data", [Array<Filter>, Array<Filter>, ExplData | undefined]]
  | ["loading", undefined]
  | ["set_all_filters", Array<Filter>]
  | ["set_all_exclude_filters", Array<Filter>]
  | ["set_init_state", [Array<Filter>, number]]
  | ["set_patch", number]
  | ["set_rank_group", number]
  | ["select_level", Array<number>]
  | ["set_item_count", string | null]
  | ["set_last_round", string | null]
  | ["select_portal", Array<number>]
  | ["select_region", Array<string>]
  | ["set_gamemode", GameMode]
  | ["set_game_type", number]
  | ["set_date", number]
  | ["set_is_patron", boolean];

const reduceAction = (state: State, [type, payload]: Action): State => {
  if (type === "set_filter") {
    let filters = state.filters.slice();
    if (payload[1] == null) {
      filters.splice(payload[0], 1);
    } else {
      const [idx, filter] = payload;
      // if (filter.typ === "i" && filter.unitId[0] === "") {
      //   const unit = state.filters.find((f): f is FilterUnit => f.typ === "u");
      //   if (unit) {
      //     filter.unitId = [unit.id, unit.starLevel];
      //   } else {
      //     return state;
      //   }
      // }
      filters[idx] = filter;
      filters = filters.filter((v) => v != null);
    }
    filters = checkItems(filters);
    return {
      ...state,
      filters,
    };
  } else if (type === "set_exclude_filter") {
    let excludeFilters = state.excludeFilters.slice();
    if (payload[1] == null) {
      excludeFilters.splice(payload[0], 1);
    } else {
      const [idx, filter] = payload;
      excludeFilters[idx] = filter;
      excludeFilters = excludeFilters.filter((v) => v != null);
    }
    // Exclude filters don't have assigned units
    // excludeFilters = checkItems(excludeFilters);
    return {
      ...state,
      excludeFilters,
    };
  } else if (type === "add_filter") {
    // const filter = payload;
    // if (filter.typ === "i" && filter.unitId[0] === "") {
    //   const unit = state.filters.find((f): f is FilterUnit => f.typ === "u");
    //   if (unit) {
    //     filter.unitId = [unit.id, unit.starLevel];
    //   } else {
    //     return state;
    //   }
    // }
    const filter = payload;
    if (filter.typ === "i" && filter.unitId != null) {
      const unassignedIdx = state.filters.findIndex(
        (f) => f.typ === "i" && f.id === filter.id && f.unitId == null,
      );
      if (unassignedIdx != -1) {
        let filters = state.filters.slice();
        filters[unassignedIdx] = {
          ...(filters[unassignedIdx] as FilterItem),
          unitId: filter.unitId,
        };
        const hasUnit = filters.some(
          (f) =>
            f.typ === "u" &&
            f.id === filter.unitId![0] &&
            f.starLevel === filter.unitId![1],
        );
        if (!hasUnit) {
          filters.push({
            typ: "u",
            id: filter.unitId[0],
            sid: filter.unitId[0],
            starLevel: filter.unitId[1],
            label: T.units(filter.unitId[0]),
            itemCount: undefined,
          });
        }
        return {
          ...state,
          filters,
        };
      }
    }
    return {
      ...state,
      filters: state.filters.concat([filter]),
    };
  } else if (type === "set_build") {
    // Remove all items for the unit
    const filters = state.filters.filter(
      (f) =>
        !(
          f.typ === "i" &&
          f.unitId?.[0] === payload.unit &&
          f.unitId?.[1] === payload.stars
        ),
    );
    for (let itemId of payload.items) {
      const pl = payload as ItemBuild;
      filters.push({
        typ: "i",
        id: itemId,
        sid: String(itemId),
        label: T.items(itemId),
        unitId: [pl.unit, pl.stars],
      });
    }
    return {
      ...state,
      filters,
    };
  } else if (type === "set_query_data") {
    return {
      ...state,
      queryData: payload,
      loading: false,
    };
  } else if (type === "loading") {
    return {
      ...state,
      loading: true,
    };
  } else if (type === "set_init_state") {
    return {
      ...state,
      filters: payload[0],
      rankGroup: payload[1],
    };
  } else if (type === "set_all_filters") {
    return {
      ...state,
      filters: payload,
    };
  } else if (type === "set_all_exclude_filters") {
    return {
      ...state,
      excludeFilters: payload,
    };
  } else if (type === "set_rank_group") {
    return {
      ...state,
      rankGroup: payload,
    };
  } else if (type === "select_level") {
    return {
      ...state,
      levels: payload,
    };
  } else if (type === "set_patch") {
    return {
      ...state,
      patch: payload,
    };
  } else if (type === "set_item_count") {
    if (payload !== state.itemCount) {
      return {
        ...state,
        itemCount: payload,
      };
    } else {
      return state;
    }
  } else if (type === "set_last_round") {
    if (payload !== state.lastRound) {
      return {
        ...state,
        lastRound: payload,
      };
    } else {
      return state;
    }
  } else if (type === "select_portal") {
    return {
      ...state,
      portals: payload,
    };
  } else if (type === "select_region") {
    return {
      ...state,
      regions: payload,
    };
  } else if (type === "set_gamemode") {
    return {
      ...state,
      gameMode: payload,
    };
  } else if (type === "set_game_type") {
    return {
      ...state,
      gameType: payload,
    };
  } else if (type === "set_date") {
    return {
      ...state,
      date: payload,
    };
  } else if (type === "set_available_patches") {
    return {
      ...state,
      availablePatches: payload,
    };
  }

  return state;
};

export const explorerData = async (
  set: number,
  isPatron: boolean,
  filters: Array<Filter>,
  excludeFilters: Array<Filter>,
  patch: number,
  rankGroup: number,
  levels: Array<number>,
  lastRound: string | null,
  itemCount: string | null,
  portals: Array<number>,
  regions: Array<string>,
  gameMode: GameMode,
  gameType: number,
  date: number,
): Promise<undefined | ExplData> => {
  if (!isPatron) {
    sendMsg(patreonErrMsg);
    return undefined;
  }

  const query: Array<any> = filtersToQuery(set, filters, false).concat(
    filtersToQuery(set, excludeFilters, true),
  );
  if (levels.length > 0) {
    query.push({
      typ: "l2",
      value: levels.join(","),
    });
  }

  if (gameType != 0) {
    query.push({
      typ: "gt",
      value: String(gameType),
    });
  }

  if (lastRound) {
    query.push({
      typ: "lr",
      value: lastRound,
    });
  }
  if (itemCount) {
    query.push({
      typ: "ic",
      value: itemCount,
    });
  }
  if (portals.length > 0) {
    query.push({
      typ: "po",
      value: portals.join(","),
    });
  }
  if (regions.length > 0) {
    query.push({
      typ: "r",
      value: regions.join(","),
    });
  }
  if (date >= 0) {
    query.push({
      typ: "dt",
      value: date == 7 ? "9" : String(date),
    });
  }

  const uid = localStorage.getItem("uid");

  // const prefix = gameMode === "ranked" ? "" : gameMode + "-";
  const queueId = getQueueId(gameMode);

  const response = await fetch(
    `https://d3.tft.tools/combos/explorer/${queueId}/${patch}/${rankGroup}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      // body: JSON.stringify(query),
      body: JSON.stringify({
        uid,
        filters: query,
      }),
    },
  );
  if (response.status === 401) {
    sendMsg(patreonErrMsg);
    return undefined;
  } else if (response.status !== 200) {
    return undefined;
  }
  const json = await response.json();
  return json;
};

let allUnitFilters_: { [set: number]: Array<FilterUnit> } = {};
let allTraitFilters_: { [set: number]: Array<FilterTrait> } = {};
let allExcludeTraitFilters_: { [set: number]: Array<FilterTrait> } = {};
let allItemFilters_: null | Array<FilterItem> = null;
let allAugmentFilters_: null | Array<FilterAugment> = null;
export const allUnitFilters = (set: number): Array<FilterUnit> => {
  if (allUnitFilters_[set] != null) {
    return allUnitFilters_[set];
  } else {
    const allUnits: Array<FilterUnit> = Sd.allUnits3(set).map((id) => {
      return {
        typ: "u",
        id,
        sid: id,
        starLevel: 0,
        label: T.units(id),
        exclude: false,
        itemCount: undefined,
      };
    });
    allUnitFilters_[set] = allUnits;
    return allUnits;
  }
};
export const allTraitFilters = (set: number): Array<FilterTrait> => {
  if (allTraitFilters_[set] != null) {
    return allTraitFilters_[set];
  } else {
    const allTraits: Array<FilterTrait> = Sd.allTraits2(set)
      .map((id) => {
        return (traitRankCounts(id, set) || []).map(
          (count, idx): FilterTrait => ({
            typ: "t",
            id,
            sid: id,
            rank: String(idx + 1),
            label: count + " " + T.traits(id),
          }),
        );
      })
      .flat();
    allTraitFilters_[set] = allTraits;
    return allTraits;
  }
};
export const allExcludeTraitFilters = (set: number): Array<FilterTrait> => {
  if (allExcludeTraitFilters_[set] != null) {
    return allExcludeTraitFilters_[set];
  } else {
    const allTraits: Array<FilterTrait> = Sd.allTraits2(set)
      .map((id) => {
        const filters = (traitRankCounts(id, set) || []).map(
          (count, idx): FilterTrait => ({
            typ: "t",
            id,
            sid: id,
            rank: String(idx + 1),
            label: count + " " + T.traits(id),
          }),
        );
        filters.unshift({
          typ: "t",
          id,
          sid: id,
          rank: String(0),
          label: T.traits(id),
        });
        return filters;
      })
      .flat();
    allExcludeTraitFilters_[set] = allTraits;
    return allTraits;
  }
};
export const allItemFilters = (): Array<FilterItem> => {
  if (allItemFilters_ !== null) {
    return allItemFilters_;
  } else {
    const allItems: Array<FilterItem> = Object.entries(Data14.items)
      .filter(
        ([sid, info]) =>
          info.id > 8 && !sid.includes("Chempunk") && !sid.endsWith("_HR"),
      )
      .map(([id, _v]) => ({
        typ: "i",
        id: id,
        sid: id,
        unitId: undefined,
        label: T.items(id),
      }));
    allItemFilters_ = allItems;
    return allItemFilters_;
  }
};
export const allAugmentFilters = (): Array<FilterAugment> => {
  if (allAugmentFilters_ !== null) {
    return allAugmentFilters_;
  } else {
    const allAugments: Array<FilterAugment> = Object.entries(Data14.augments)
      .filter(([_id, v]) => v.typ != "hr")
      .map(([id, _v]) => {
        let altLabel = undefined;
        return {
          typ: "a",
          id: id,
          sid: id,
          pick: 0,
          label: T.augments(id),
          altLabel,
        };
      });
    allAugmentFilters_ = allAugments;
    return allAugments;
  }
};

const loadPatronStatus = () =>
  typeof window !== "undefined" &&
  (localStorage.getItem("afmt") == "yay" ||
    localStorage.getItem("p-status") != null);

export const useCoState = (
  path?: string,
): [
  State,
  (action: Action) => void,
  Array<Filter>,
  Array<Filter>,
  (search: string) => Array<Filter>,
  (search: string) => Array<Filter>,
] => {
  const router = useRouter();
  const [initConfig, initFilters, initExcludeFilters] = decodeQuery(path || "");
  const [state, dispatch] = useReducer(reduceAction, {
    isPatron: loadPatronStatus(),
    availablePatches: [],
    filters: initFilters,
    excludeFilters: initExcludeFilters,
    queryData: undefined,
    loading: false,
    rankGroup: initConfig.rankGroup,
    patch: initConfig.patch,
    levels: initConfig.levels,
    itemCount: initConfig.itemCount,
    lastRound: initConfig.lastRound,
    portals: initConfig.portals,
    regions: initConfig.regions,
    gameMode: initConfig.gameMode,
    gameType: initConfig.gameType,
    date: initConfig.date,
  });

  useEffect(() => {
    dispatch(["set_is_patron", loadPatronStatus()]);
  }, []);

  const set =
    state.gameMode === "temp" ? 101 : state.gameMode === "pbe" ? 140 : statsSet;

  useEffect(() => {
    const update = () => {
      fetch(`https://d3.tft.tools/combos/config`)
        .then((resp) => resp.json())
        .then((config) => {
          dispatch(["set_available_patches", config.patches]);
        })
        .catch((err) => {
          console.log("Config err", err);
        });
    };
    update();
    const interval = setInterval(update, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const includeFilterOptions = useMemo(() => {
    const allUnits: Array<FilterUnit> = allUnitFilters(set);
    const allTraits: Array<FilterTrait> = allTraitFilters(set);
    const allItems: Array<FilterItem> = allItemFilters();
    // const allAugments: Array<FilterAugment> = allAugmentFilters();

    // return [...allUnits, ...allItems, ...allTraits, ...allAugments];
    return [...allUnits, ...allItems, ...allTraits];
  }, [set]);
  const excludeFilterOptions = useMemo(() => {
    const allUnits: Array<FilterUnit> = allUnitFilters(set);
    const allTraits: Array<FilterTrait> = allExcludeTraitFilters(set);
    const allItems: Array<FilterItem> = allItemFilters();
    // const allAugments: Array<FilterAugment> = allAugmentFilters();

    // return [...allUnits, ...allItems, ...allTraits, ...allAugments];
    return [...allUnits, ...allItems, ...allTraits];
  }, [set]);

  const getMatches = useCallback(
    (search: string) => {
      const matches: Array<Filter> = [];

      const allUnits: Array<FilterUnit> = allUnitFilters(set);
      let unitMatches = Search.searchUnitsExact(search, set);
      allUnits.forEach((v) => {
        if (unitMatches.has(v.id)) {
          matches.push(v);
        }
      });

      const allItems: Array<FilterItem> = allItemFilters();
      let itemMatches = Search.searchItems(search, set);
      allItems.forEach((v) => {
        if (itemMatches.has(v.id)) {
          matches.push(v);
        }
      });

      const allTraits: Array<FilterTrait> = allTraitFilters(set);
      let traitMatches = Search.searchRankTraits(search, set);
      allTraits.forEach((v) => {
        if (traitMatches.has(`${v.id}-${v.rank}`)) {
          matches.push(v);
        }
      });

      // const allAugments: Array<FilterAugment> = allAugmentFilters();
      // let augMatches = Search.searchAugments(search, set);
      // allAugments.forEach((v) => {
      //   if (augMatches.has(v.id)) {
      //     matches.push(v);
      //   }
      // });

      return matches;
    },
    [set],
  );

  const getExcludeMatches = useCallback(
    (search: string) => {
      const matches: Array<Filter> = [];

      const allUnits: Array<FilterUnit> = allUnitFilters(set);
      let unitMatches = Search.searchUnitsExact(search, set);
      allUnits.forEach((v) => {
        if (unitMatches.has(v.id)) {
          matches.push(v);
        }
      });

      const allItems: Array<FilterItem> = allItemFilters();
      let itemMatches = Search.searchItems(search, set);
      allItems.forEach((v) => {
        if (itemMatches.has(v.id)) {
          matches.push(v);
        }
      });

      const allTraits: Array<FilterTrait> = allExcludeTraitFilters(set);
      let traitMatches = Search.searchRankTraits(search, set);
      allTraits.forEach((v) => {
        if (traitMatches.has(`${v.id}-${v.rank == "0" ? "1" : v.rank}`)) {
          matches.push(v);
        }
      });

      // const allAugments: Array<FilterAugment> = allAugmentFilters();
      // let augMatches = Search.searchAugments(search, set);
      // allAugments.forEach((v) => {
      //   if (augMatches.has(v.id)) {
      //     matches.push(v);
      //   }
      // });

      return matches;
    },
    [set],
  );

  const prevQuery = useRef([
    [], // state.filters,
    [], // state.excludeFilters,
    lastPatch, // state.patch,
    1, // state.rankGroup,
    [], // state.levels,
    null, // state.lastRound,
    null, // state.itemCount,
    [], // state.portals,
    [], // state.regions,
    "ranked", // state.gameMode,
    0, // state.gameType
    -1, // state.date
  ] as any);

  useEffect(() => {
    let cancelled = false;
    if (
      state.filters.length > 0 ||
      state.excludeFilters.length > 0 ||
      state.lastRound != null ||
      state.itemCount != null ||
      state.levels.length > 0 ||
      state.portals.length > 0 ||
      state.regions.length > 0 ||
      state.gameType !== 0 ||
      state.date >= 0
    ) {
      if (
        !deepEqual(state.filters, prevQuery.current[0]) ||
        !deepEqual(state.excludeFilters, prevQuery.current[1]) ||
        state.patch !== prevQuery.current[2] ||
        state.rankGroup !== prevQuery.current[3] ||
        state.levels !== prevQuery.current[4] ||
        state.lastRound !== prevQuery.current[5] ||
        state.itemCount !== prevQuery.current[6] ||
        state.portals !== prevQuery.current[7] ||
        state.regions !== prevQuery.current[8] ||
        state.gameMode !== prevQuery.current[9] ||
        state.gameType !== prevQuery.current[10] ||
        state.date !== prevQuery.current[11]
      ) {
        const query = encodeQuery(
          set,
          {
            patch: state.patch,
            rankGroup: state.rankGroup,
            levels: state.levels,
            lastRound: state.lastRound,
            itemCount: state.itemCount,
            portals: state.portals,
            regions: state.regions,
            gameMode: state.gameMode,
            gameType: state.gameType,
            date: state.date,
          },
          state.filters,
          state.excludeFilters,
        );
        const locPrefix = router.locale === "en" ? "" : "/" + router.locale;
        window.history.replaceState(
          {},
          "",
          `${locPrefix}/explorer-advanced/${query}`,
        );
        dispatch(["loading", undefined]);

        explorerData(
          set,
          state.isPatron,
          state.filters,
          state.excludeFilters,
          state.patch,
          state.rankGroup,
          state.levels,
          state.lastRound,
          state.itemCount,
          state.portals,
          state.regions,
          state.gameMode,
          state.gameType,
          state.date,
        ).then((data) => {
          if (!cancelled) {
            if (data) {
              dispatch([
                "set_query_data",
                [state.filters, state.excludeFilters, data],
              ]);
            } else {
              dispatch([
                "set_query_data",
                [state.filters, state.excludeFilters, undefined],
              ]);
            }
          }
        });
        prevQuery.current = [
          state.filters,
          state.excludeFilters,
          state.patch,
          state.rankGroup,
          state.levels,
          state.lastRound,
          state.itemCount,
          state.portals,
          state.regions,
          state.gameMode,
          state.gameType,
          state.date,
        ];
      }
    } else {
      const uid = localStorage.getItem("uid");
      // const prefix = state.gameMode === "ranked" ? "" : state.gameMode + "-";
      const queueId = getQueueId(state.gameMode);
      const query = encodeQuery(
        set,
        {
          patch: state.patch,
          rankGroup: state.rankGroup,
          levels: state.levels,
          lastRound: state.lastRound,
          itemCount: state.itemCount,
          portals: state.portals,
          regions: state.regions,
          gameMode: state.gameMode,
          gameType: state.gameType,
          date: state.date,
        },
        state.filters,
        state.excludeFilters,
      );
      const locPrefix = router.locale === "en" ? "" : "/" + router.locale;
      window.history.replaceState(
        {},
        "",
        `${locPrefix}/explorer-advanced/${query}`,
      );
      fetch(
        `https://d3.tft.tools/combos/base/${queueId}/${state.patch}/${state.rankGroup}`,

        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: `{"uid": "${uid}"}`,
        },
      )
        .then((resp) => {
          if (resp.status === 401) {
            sendMsg(patreonErrMsg);
            return undefined;
          } else if (resp.status !== 200) {
            return undefined;
          } else {
            return resp.json();
          }
        })
        .then((data) => {
          if (!cancelled) {
            dispatch(["set_query_data", [[], [], data]]);
          }
        });
      prevQuery.current = [
        state.filters,
        state.excludeFilters,
        state.patch,
        state.rankGroup,
        state.levels,
        state.lastRound,
        state.itemCount,
        state.portals,
        state.regions,
        state.gameMode,
        state.gameType,
        state.date,
      ];
    }

    return () => {
      cancelled = true;
    };
  }, [
    state.filters,
    state.excludeFilters,
    state.patch,
    state.rankGroup,
    state.levels,
    state.lastRound,
    state.itemCount,
    state.portals,
    state.regions,
    state.gameMode,
    state.gameType,
    state.date,
  ]);

  return [
    state,
    dispatch,
    includeFilterOptions,
    excludeFilterOptions,
    getMatches,
    getExcludeMatches,
  ];
};
